import { useCallback, useMemo, useRef, useEffect } from 'react';

import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
  generatePath
} from 'react-router-dom';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { PageViewedTracker } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useIdentity } from '@zenbusiness/application-commons-identity';
import { useVisit } from '@zenbusiness/application-commons-visit';

import AssistantWelcome from '#client/components/AssistantWelcome';
import ChatConnectionError from '#client/components/ChatConnectionError';
import ChatUI from '#client/components/ChatUI';
import Transition from '#client/components/Transition';
import { CLICKSTREAM_COMMON_PROPS } from '#client/components/utils/constants';
import * as ROUTES from '#client/constants/routes';
import { isValidWarmStartCode } from '#client/constants/warmStartMessages';
import { useUnifiedBusinessEntityContext } from '#client/contexts/UnifiedBusinessEntityProvider';
import { useDelayedLoading } from '#client/hooks/useDelayedLoading';
import { useInstructionRenderer } from '#client/hooks/useInstructionRenderer';
import { useThreadAuthentication } from '#client/hooks/useThreadAuthentication';
import useUserMode from '#client/hooks/useUserMode';

/**
 * ThreadViewer Component
 *
 * Handles the /guide/thread/:threadUuid route
 * Responsible for:
 * 1. Loading existing thread messages
 * 2. Continuing chat conversation
 * 3. Handling invalid/expired threadUuid (redirect to ThreadCreator)
 * 4. Handling network/timeout errors with retry logic
 * 5. Auto-refreshing sidebar when arriving from thread creation
 */

export default function ThreadViewer() {
  const { threadUuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const enableMultiVeloMode = useExperimentFlag('enable-multi-velo-mode', true);
  const { authenticated, userUuid } = useAuthentication();
  const { loading: entityLoading, businessEntityUuid } = useUnifiedBusinessEntityContext();
  const { loading: identityLoading } = useIdentity();
  const { visitUuid } = useVisit();

  // Extract topic parameter from URL
  const topicCode = searchParams.get('topic');
  const isValidTopic = topicCode && isValidWarmStartCode(topicCode);

  // Use the existing instruction renderer with the threadUuid from URL
  const {
    addUserResponse,
    currentMode,
    currentThreadUuid,
    threadMode,
    isError,
    hasNextInstruction,
    initialized,
    isComplete,
    instructions,
    loading,
    loadingNext,
    loadExistingMessages,
    loadNextInstruction,
    resetStateForNewThread,
    retryConnection,
    sendUserFeedback,
    setInstructions
  } = useInstructionRenderer();

  // Get user mode (needs threadMode from useInstructionRenderer)
  const userMode = useUserMode(threadMode);

  // Debounced loading for thread switches - prevents flash on quick switches
  const showThreadLoading = useDelayedLoading(loading && !initialized, 250);

  // Ref to communicate with ActivePromptInput (for prompt button population)
  const activePromptInputRef = useRef(null);

  // Track whether we've already used the initial load flag to prevent reuse on refresh
  const hasUsedInitialLoadRef = useRef(false);

  const isInitialLoad = location.state?.isInitialLoad && !hasUsedInitialLoadRef.current;
  const effectiveMode = isInitialLoad ? 'initial' : currentMode;

  const redirectTo = searchParams.get('redirectTo');

  // Authentication check that respects anonymous mode
  // Uses custom hook to avoid redirecting anonymous/standalone users
  useThreadAuthentication(
    userMode,
    authenticated,
    threadUuid,
    redirectTo,
    useCallback(() => {
      // onAuthRequired - called when authentication is required
      // No state machine dispatch needed in simple ThreadViewer
    }, []),
    useCallback(() => {
      // onAuthValid - called when authentication is valid or not required
      // No state machine dispatch needed in simple ThreadViewer
    }, [])
  );

  // Load existing messages on mount
  useEffect(
    () => {
      if (!enableMultiVeloMode && !userMode.isBusiness) {
        navigate('/', { replace: true });
        return;
      }

      if (currentThreadUuid && currentThreadUuid !== threadUuid) {
        resetStateForNewThread(threadUuid);
      }

      // Check if we have the required identity fields for the current mode
      const hasRequiredIdentity = () => {
        if (!threadUuid || initialized || !userMode) return false;

        // Anonymous and standalone modes only need visitUuid
        if (userMode.isAnonymous || userMode.isStandalone) {
          return Boolean(visitUuid);
        }

        // Starter mode needs visitUuid + userUuid
        if (userMode.isStarter) {
          return Boolean(visitUuid && userUuid);
        }

        // Business mode needs all three
        return Boolean(visitUuid && userUuid && businessEntityUuid);
      };

      if (hasRequiredIdentity()) {
        // Add error handling to prevent unhandled promise rejection
        loadExistingMessages(
          { visitUuid, userUuid, businessEntityUuid },
          threadUuid,
          userMode.mode
        ).catch(() => {
          // Error is handled by useInstructionRenderer (sets isError state)
          // Catch here to prevent bubbling to ErrorBoundary
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      enableMultiVeloMode,
      threadUuid,
      loadExistingMessages,
      visitUuid,
      userUuid,
      businessEntityUuid,
      userMode,
      resetStateForNewThread,
      currentThreadUuid,
      initialized
    ]
  );

  // Only mark the initial load flag as used after we have instructions and have rendered
  // This prevents premature marking during the loading phase
  useEffect(() => {
    if (isInitialLoad && instructions.length > 0 && !loading) {
      hasUsedInitialLoadRef.current = true;
    }
  }, [isInitialLoad, instructions.length, loading]);

  // Determine if we should show welcome screen vs chat UI
  // Only show welcome when we're truly in an initial state (no thread loading, no instructions)
  const shouldShowWelcome = useMemo(() => {
    // Don't show welcome if we're loading a thread
    if (loading || showThreadLoading) {
      return false;
    }

    // Don't show welcome if we have instructions
    if (instructions.length > 0) {
      return false;
    }

    // Show welcome only when we're in a stable, empty state
    return initialized && !loading;
  }, [loading, showThreadLoading, instructions.length, initialized]);

  /**
   * Handler for when user sends a message (either typed or from prompt button)
   * @param message
   * @param messageType
   * @param agentName
   */
  const handleMessageSend = useCallback(
    async (message, messageType = 'text', agentName = null) => {
      try {
        await loadNextInstruction(message, messageType, userMode.mode, {
          visitUuid,
          userUuid,
          businessEntityUuid,
          agentName
        });
      } catch (err) {
        // Error is handled by useInstructionRenderer (sets isError state)
        // Catch here to prevent bubbling to ErrorBoundary
      }
    },
    [loadNextInstruction, visitUuid, userUuid, businessEntityUuid, userMode]
  );

  const handleUserFeedback = useCallback(
    async (messageUuid, feedback) => {
      try {
        await sendUserFeedback(
          { visitUuid, userUuid, businessEntityUuid },
          messageUuid,
          feedback,
          userMode.mode
        );
      } catch (err) {
        // Error is handled by useInstructionRenderer
        // Catch here to prevent bubbling to ErrorBoundary
      }
    },
    [sendUserFeedback, visitUuid, userUuid, businessEntityUuid, userMode]
  );

  const handleGreeting = useCallback(async () => {
    try {
      await loadNextInstruction('greet', 'greet', userMode.mode, {
        visitUuid,
        userUuid,
        businessEntityUuid
      });
    } catch (err) {
      // Error is handled by useInstructionRenderer
      // Catch here to prevent bubbling to ErrorBoundary
    }
  }, [loadNextInstruction, visitUuid, userUuid, businessEntityUuid, userMode]);

  /**
   * Handler for when user clicks a prompt button
   * Instead of immediately sending, populate the input field
   * @param promptText
   */
  const handlePromptSelect = useCallback((promptText) => {
    // Use ref to directly set the input value without causing re-renders
    if (activePromptInputRef.current && activePromptInputRef.current.setValue) {
      activePromptInputRef.current.setValue(promptText);
    }
  }, []);

  // Handle start new
  const handleStartNew = useCallback(() => {
    // Determine the correct path based on user mode
    let basePath;

    if (userMode && (userMode.isAnonymous || userMode.isStandalone)) {
      // Anonymous/Standalone mode: use visitor route or chat entry
      basePath = ROUTES.chatEntry; // Universal entry point
    } else if (businessEntityUuid) {
      // Business/Starter mode: use company-specific route
      basePath = generatePath(ROUTES.threadCreator, { businessEntityUuid });
    } else {
      // Fallback to universal chat entry
      basePath = ROUTES.chatEntry;
    }

    // Preserve topic parameter if present
    if (isValidTopic) {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set('topic', topicCode);
      navigate(`${basePath}?${newSearchParams.toString()}`, {
        replace: true
      });
    } else {
      navigate(basePath, {
        replace: true
      });
    }
  }, [businessEntityUuid, navigate, isValidTopic, topicCode, userMode]);

  // Handle retry logic
  const handleRetry = useCallback(async () => {
    if (threadUuid) {
      // We have a threadUuid from URL, try to reload the existing thread
      if (retryConnection) {
        try {
          await retryConnection({ visitUuid, userUuid, businessEntityUuid }, userMode.mode);
        } catch (err) {
          // Error is handled by useInstructionRenderer
          // Catch here to prevent bubbling to ErrorBoundary
        }
      }
    } else {
      // No threadUuid, start a new conversation instead
      handleStartNew();
    }
  }, [
    businessEntityUuid,
    handleStartNew,
    retryConnection,
    threadUuid,
    visitUuid,
    userUuid,
    userMode
  ]);

  // Memoize the props to prevent unnecessary re-renders
  const chatUIProps = useMemo(
    () => ({
      activePromptInputRef,
      addUserResponse,
      currentMode: effectiveMode,
      isError,
      hasNextInstruction,
      instructions,
      isComplete,
      loading,
      loadingNext,
      onMessageSend: handleMessageSend,
      sendUserFeedback: handleUserFeedback,
      setInstructions
    }),
    [
      activePromptInputRef,
      addUserResponse,
      effectiveMode,
      isError,
      handleMessageSend,
      handleUserFeedback,
      hasNextInstruction,
      instructions,
      isComplete,
      loading,
      loadingNext,
      setInstructions
    ]
  );

  // Memoize the props to prevent unnecessary re-renders
  const welcomeProps = useMemo(
    () => ({
      onPromptSelect: handlePromptSelect,
      onMessageSend: handleMessageSend,
      onGreeting: handleGreeting,
      activePromptInputRef
    }),
    [handlePromptSelect, handleMessageSend, handleGreeting]
  );

  // ========================================================================
  // OPTION A: CASE-STYLE RENDERING (Similar to StateRenderer but simpler)
  // ========================================================================
  // Uncomment this block to use case-style rendering with simple derived state

  // Derive simple state from flags (no complex state machine needed!)
  const getCurrentState = () => {
    // Check in priority order
    if (entityLoading || identityLoading) return 'INITIALIZING';

    // Only show AUTHENTICATING if we're in a mode that requires auth AND not authenticated
    // Anonymous and standalone modes don't require authentication
    if (!authenticated && userMode && !userMode.isAnonymous && !userMode.isStandalone) {
      return 'AUTHENTICATING';
    }

    if (showThreadLoading) return 'LOADING';
    if (isError) return 'ERROR';
    if (shouldShowWelcome) return 'WELCOME';
    return 'CHATTING';
  };

  const currentState = getCurrentState();

  // Render based on derived state (clean case statement!)
  const renderContent = () => {
    switch (currentState) {
      case 'INITIALIZING':
      case 'AUTHENTICATING':
      case 'LOADING':
        return <Transition type="THREAD_LOADING" />;

      case 'WELCOME':
        return <AssistantWelcome {...welcomeProps} />;

      case 'CHATTING':
        return <ChatUI {...chatUIProps} />;

      case 'ERROR':
        return (
          <ChatConnectionError
            instructions={instructions}
            onRetry={handleRetry}
            onStartNew={handleStartNew}
          />
        );

      default:
        console.error(`Unknown state: ${currentState}`);
        return <Transition type="THREAD_LOADING" />;
    }
  };

  return (
    <>
      <PageViewedTracker {...CLICKSTREAM_COMMON_PROPS} appPageName="thread_viewer" />
      {renderContent()}
    </>
  );
}
